import Big from 'big.js';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import SharesInput from '@/modules/common/components/SharesCountInput.tsx';
import { queryClient } from '@/modules/common/query.ts';
import { CURRENT_USER_ACTIVITIES_QUERY_KEY } from '@/modules/investor-dashboard/queries/activity.tsx';
import { useTransactionAwaitingPaymentMutation } from '@/modules/investor-dashboard/queries/transactions.tsx';
import { useGetRegister } from '@/modules/issuer-dashboard/hooks/useGetRegister.tsx';
import { Button, ModalContentWrapper } from 'ui';
import { goodFormatUSDCurrency } from 'ui/utils';
import { ActivitiesPaginateResponse } from 'api';

interface InvestorConfirmBuyBackModalProps {
  open: boolean;
  activity: ActivitiesPaginateResponse;
  onClose: () => void;
}

const InvestorConfirmBuyBackModal = ({
  open,
  activity,
  onClose,
}: InvestorConfirmBuyBackModalProps) => {
  const [sharesQuantity, setSharesQuantity] = useState<number | string>(activity.quantity);
  const { mutateAsync, isLoading } = useTransactionAwaitingPaymentMutation(
    activity.register_id || 0,
  );

  const register = useGetRegister(activity.register_id);

  const isAllowedEditionBuyBack = true; // todo: change after implementing on server

  const handleConfirm = useCallback(async () => {
    const isoStringWithOffset = format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXXX");
    await mutateAsync({
      id: activity.id,
      issue_date: isoStringWithOffset,
      quantity: +sharesQuantity || 0,
    });
    queryClient.removeQueries([CURRENT_USER_ACTIVITIES_QUERY_KEY]);
    onClose();
  }, [activity.id, mutateAsync, onClose, sharesQuantity]);

  return (
    <ModalContentWrapper title="Confirm buy back" open={open} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <p>
          Would you like to accept this buy back? After you accept it, your issuer needs to confirm
          it. We will email you once it's done.
        </p>
        {isAllowedEditionBuyBack ? (
          <div>
            <p>
              Price per share/unit: <b>{goodFormatUSDCurrency(activity.price_per_share || 0)}</b>.
              Total:{' '}
              <b>
                {goodFormatUSDCurrency(
                  new Big(activity.price_per_share || 0).mul(sharesQuantity || 0).toNumber(),
                )}
              </b>
              .
            </p>
            <p>
              <SharesInput
                name="amount"
                value={sharesQuantity}
                allowFractional={register?.allows_fractional_shares}
                label="Enter amount of shares, you want to give:"
                onChange={setSharesQuantity}
              />
            </p>
          </div>
        ) : (
          <p>
            Price per share/unit: <b>{goodFormatUSDCurrency(activity.price_per_share || 0)}</b>,
            total: <b>{goodFormatUSDCurrency(activity.amount)}</b>
          </p>
        )}

        <Button onClick={handleConfirm} loading={isLoading}>
          Confirm buy back
        </Button>
      </div>
    </ModalContentWrapper>
  );
};

export default InvestorConfirmBuyBackModal;
